import { useState } from "react";
import { TOOLS_25, SKILLS_20 } from "@/lib/tools25";
import { Wrench, Sparkles, Play, Send, Mail, ListChecks, MessagesSquare } from "lucide-react";
import { toast } from "sonner";
import { useNeural, sha512 } from "@/store/neuralStore";
import { sandboxDB } from "@/lib/db";
import { supabase } from "@/integrations/supabase/client";
import { notifyError } from "@/lib/errorToast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const CATS = ["All", "Orchestration", "Reasoning", "Memory", "Cost", "Lifecycle", "Web", "Code", "Document", "Media", "Data", "RAG", "Trigger", "Connectors"] as const;

type RealTool = {
  id: string;
  name: string;
  desc: string;
  fn: string;
  icon: typeof Send;
  hue: number;
  category: "Connectors";
  active: true;
  fields: { id: string; label: string; placeholder?: string; type?: "text" | "textarea" }[];
};

const REAL_TOOLS: RealTool[] = [
  { id: "slack-post", name: "Slack · post message", desc: "Posta mensagem em canal Slack via OAuth gateway.",
    fn: "slack-post", icon: Send, hue: 145, category: "Connectors", active: true,
    fields: [{ id: "channel", label: "Canal", placeholder: "#general" }, { id: "text", label: "Mensagem", type: "textarea" }] },
  { id: "slack-list", name: "Slack · list channels", desc: "Lista canais públicos do workspace.",
    fn: "slack-list", icon: ListChecks, hue: 200, category: "Connectors", active: true, fields: [] },
  { id: "gmail-send", name: "Gmail · send email", desc: "Envia email via Gmail.",
    fn: "gmail-send", icon: Mail, hue: 0, category: "Connectors", active: true,
    fields: [{ id: "to", label: "Para" }, { id: "subject", label: "Assunto" }, { id: "text", label: "Corpo", type: "textarea" }] },
  { id: "gmail-list", name: "Gmail · list inbox", desc: "Lista mensagens recentes da inbox.",
    fn: "gmail-list", icon: MessagesSquare, hue: 220, category: "Connectors", active: true,
    fields: [{ id: "q", label: "Filtro", placeholder: "is:unread" }] },
];

export function ToolsGalaxy() {
  const [cat, setCat] = useState<(typeof CATS)[number]>("All");
  const pushRun = useNeural((s) => s.pushRun);
  const [activeReal, setActiveReal] = useState<RealTool | null>(null);
  const [vals, setVals] = useState<Record<string, string>>({});
  const [running, setRunning] = useState(false);

  const allTools = [
    ...TOOLS_25.map((t) => ({ ...t, kind: "tool" as const })),
    ...SKILLS_20.map((s) => ({ ...s, kind: "skill" as const })),
    ...REAL_TOOLS.map((r) => ({ id: r.id, name: r.name, desc: r.desc, icon: r.icon, hue: r.hue, category: r.category, active: r.active, kind: "connector" as const })),
  ];
  const list = allTools.filter((t) => cat === "All" || t.category === cat);

  const runTool = async (item: typeof allTools[number]) => {
    const real = REAL_TOOLS.find((r) => r.id === item.id);
    if (real) { setActiveReal(real); setVals({}); return; }

    const fn = item.kind === "skill" ? "skill-runner" : "tool-runner";
    const body = item.kind === "skill" ? { skill_id: item.id, input: {} } : { tool_id: item.id, input: {} };
    const t0 = performance.now();
    pushRun({ tool: item.name, input: JSON.stringify(body), output: "calling LLM...", status: "running", ms: 0, hash: "0x…" });
    try {
      const { data, error } = await supabase.functions.invoke(fn, { body });
      if (error) throw error;
      const errMsg = (data as { error?: string })?.error;
      if (errMsg) throw new Error(errMsg);
      const ms = Math.round(performance.now() - t0);
      const output = JSON.stringify(data);
      const hash = await sha512(JSON.stringify(body) + output);
      pushRun({ tool: item.name, input: JSON.stringify(body), output, status: "ok", ms, hash });
      toast.success(`${item.name} · ${ms}ms · sha:${hash.slice(0, 8)}`);
    } catch (e) {
      const ms = Math.round(performance.now() - t0);
      const output = JSON.stringify({ error: e instanceof Error ? e.message : String(e) });
      pushRun({ tool: item.name, input: JSON.stringify(body), output, status: "error", ms, hash: "err" });
      sandboxDB.log({ tool_id: item.id, tool_name: item.name, input: body, output, hash: "err", duration_ms: ms, status: "failed" }).catch(() => {});
      notifyError(e as Error, `${item.name} falhou`);
    }
  };

  const runRealTool = async () => {
    if (!activeReal) return;
    setRunning(true);
    const t0 = performance.now();
    const input = JSON.stringify({ tool: activeReal.id, body: vals, ts: Date.now() });
    pushRun({ tool: activeReal.name, input, output: "calling...", status: "running", ms: 0, hash: "0x…" });
    try {
      const { data, error } = await supabase.functions.invoke(activeReal.fn, { body: vals });
      if (error) throw error;
      const errMsg = (data as { error?: string })?.error;
      if (errMsg) throw new Error(errMsg);
      const output = JSON.stringify(data);
      const hash = await sha512(input + output);
      const ms = Math.round(performance.now() - t0);
      pushRun({ tool: activeReal.name, input, output, status: "ok", ms, hash });
      sandboxDB.log({ tool_id: activeReal.id, tool_name: activeReal.name, input, output, hash, duration_ms: ms, status: "success" }).catch(() => {});
      toast.success(`${activeReal.name} OK · ${ms}ms`);
      setActiveReal(null);
    } catch (e) {
      const ms = Math.round(performance.now() - t0);
      const output = JSON.stringify({ error: e instanceof Error ? e.message : String(e) });
      const hash = await sha512(input + output);
      pushRun({ tool: activeReal.name, input, output, status: "error", ms, hash });
      sandboxDB.log({ tool_id: activeReal.id, tool_name: activeReal.name, input, output, hash, duration_ms: ms, status: "failed" }).catch(() => {});
      notifyError(e as Error, `${activeReal.name} falhou`);
    }
    setRunning(false);
  };

  return (
    <div className="h-full overflow-y-auto p-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold flex items-center gap-2">
            <Wrench className="w-6 h-6 text-primary" /> Tools Galaxy
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            {TOOLS_25.length + REAL_TOOLS.length} tools · 4 reais (Slack/Gmail) · sandboxed · SHA-512 audited
          </p>
        </div>
        <div className="flex items-center gap-1 p-0.5 rounded-md bg-surface-2 border border-border" role="tablist" aria-label="Categorias">
          {CATS.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              role="tab"
              aria-selected={cat === c}
              aria-label={`Filtrar ${c}`}
              className={`text-[11px] font-mono px-2.5 py-1 rounded ${cat === c ? "bg-surface-3 text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {list.map((t) => {
          const Icon = t.icon;
          const isReal = REAL_TOOLS.some((r) => r.id === t.id);
          return (
            <div
              key={t.id}
              className="relative group glass rounded-xl p-4 hover:border-primary/40 transition-all overflow-hidden"
              style={{ boxShadow: `inset 0 0 0 1px hsl(${t.hue} 80% 30% / 0.25)` }}
            >
              <div
                className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-30 blur-2xl group-hover:opacity-60 transition-opacity"
                style={{ background: `hsl(${t.hue} 95% 55%)` }}
              />
              <div className="flex items-start justify-between mb-2">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ background: `hsl(${t.hue} 90% 12%)`, boxShadow: `0 0 14px hsl(${t.hue} 95% 55% / 0.4)` }}
                >
                  <Icon className="w-4 h-4" style={{ color: `hsl(${t.hue} 95% 70%)` }} />
                </div>
                {isReal ? (
                  <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-accent/15 text-accent border border-accent/30">
                    REAL
                  </span>
                ) : t.active ? (
                  <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-success/15 text-success border border-success/30">
                    LIVE
                  </span>
                ) : null}
              </div>
              <div className="font-display font-semibold text-sm mb-1">{t.name}</div>
              <p className="text-[11px] text-muted-foreground leading-snug min-h-[44px]">{t.desc}</p>
              <div className="mt-3 flex items-center justify-between">
                <span className="text-[9px] font-mono text-muted-foreground uppercase tracking-wider">
                  {t.category ?? "General"}
                </span>
                <button
                  onClick={() => runTool(t)}
                  aria-label={`Executar ${t.name}`}
                  className="text-[10px] font-mono px-2 py-1 rounded border border-border hover:border-primary/60 hover:text-primary flex items-center gap-1"
                >
                  <Play className="w-2.5 h-2.5" /> Run
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-[11px] text-muted-foreground font-mono flex items-center gap-2 pt-2">
        <Sparkles className="w-3 h-3 text-secondary" />
        Tools REAL chamam edge functions com OAuth. Tools simuladas geram SHA-512 e gravam em sandbox_runs.
      </div>

      {/* Diálogo de execução de tool real */}
      <Dialog open={!!activeReal} onOpenChange={(v) => !v && setActiveReal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {activeReal && <activeReal.icon className="w-4 h-4 text-primary" />}
              {activeReal?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {activeReal?.fields.length === 0 && (
              <p className="text-xs text-muted-foreground">Sem parâmetros — clique em executar.</p>
            )}
            {activeReal?.fields.map((f) => (
              <div key={f.id}>
                <Label className="text-xs">{f.label}</Label>
                {f.type === "textarea" ? (
                  <Textarea
                    value={vals[f.id] ?? ""}
                    onChange={(e) => setVals((v) => ({ ...v, [f.id]: e.target.value }))}
                    placeholder={f.placeholder}
                    rows={3}
                    aria-label={f.label}
                  />
                ) : (
                  <Input
                    value={vals[f.id] ?? ""}
                    onChange={(e) => setVals((v) => ({ ...v, [f.id]: e.target.value }))}
                    placeholder={f.placeholder}
                    aria-label={f.label}
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActiveReal(null)}>Cancelar</Button>
            <Button onClick={runRealTool} disabled={running} className="gap-1.5">
              <Play className="w-3.5 h-3.5" /> {running ? "Executando..." : "Executar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
